/**
 * Pledge Modal (Pantsättning / Hävning av pantsättning)
 *
 * Modal for registering pledge and unpledge transactions.
 * When shares are pledged, they are used as collateral for a loan
 * but ownership remains with the shareholder.
 */

import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Lock, Unlock, Info } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/common/Modal";
import { Button } from "@/components/ui/button";
import {
  useShareClasses,
  useShareholdersSummary,
  useShareholderHoldings,
  useRegisterPledge,
  useRegisterUnpledge,
} from "@/hooks/use-share-book";
import type { ShareClass, ShareholderWithHoldings, ShareHolding } from "@/lib/api";

interface PledgeModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
}

type PledgeType = "pledge" | "unpledge";

const pledgeSchema = z.object({
  pledgeType: z.enum(["pledge", "unpledge"]),
  shareholderId: z.string().min(1, "Välj aktieägare"),
  shareClassId: z.string().min(1, "Välj aktieslag"),
  numberFrom: z.coerce.number().min(1, "Aktienummer från krävs"),
  numberTo: z.coerce.number().min(1, "Aktienummer till krävs"),
  pledgedTo: z.string().optional(),
  transactionDate: z.string().min(1, "Transaktionsdatum krävs"),
  notes: z.string().optional(),
}).refine((data) => data.numberTo >= data.numberFrom, {
  message: "Aktienummer till måste vara större än eller lika med från",
  path: ["numberTo"],
}).refine((data) => {
  // pledgedTo is required for pledge, not for unpledge
  if (data.pledgeType === "pledge" && !data.pledgedTo) {
    return false;
  }
  return true;
}, {
  message: "Panthavare krävs",
  path: ["pledgedTo"],
});

type PledgeFormData = z.infer<typeof pledgeSchema>;

export function PledgeModal({ isOpen, onClose, companyId }: PledgeModalProps) {
  const [pledgeType, setPledgeType] = useState<PledgeType>("pledge");
  const { data: shareClasses = [] } = useShareClasses(companyId);
  const { data: shareholders = [] } = useShareholdersSummary(companyId);
  const registerPledge = useRegisterPledge();
  const registerUnpledge = useRegisterUnpledge();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<PledgeFormData>({
    resolver: zodResolver(pledgeSchema),
    defaultValues: {
      pledgeType: "pledge",
      shareholderId: "",
      shareClassId: "",
      numberFrom: undefined,
      numberTo: undefined,
      pledgedTo: "",
      transactionDate: new Date().toISOString().split("T")[0],
      notes: "",
    },
  });

  const watchShareholderId = watch("shareholderId");
  const watchShareClassId = watch("shareClassId");
  const watchNumberFrom = watch("numberFrom");
  const watchNumberTo = watch("numberTo");

  // Fetch holdings for selected shareholder
  const { data: holdings = [] } = useShareholderHoldings(
    watchShareholderId || undefined
  );

  // Active shareholders
  const activeShareholders = useMemo(() => {
    return shareholders.filter((s: ShareholderWithHoldings) => s.isActive);
  }, [shareholders]);

  // Filter shareholders that have holdings in selected share class
  const shareholdersWithHoldings = useMemo(() => {
    if (!watchShareClassId) return activeShareholders;
    return activeShareholders.filter((s: ShareholderWithHoldings) =>
      s.holdings.some(
        (h) => h.shareClass.id === watchShareClassId && h.totalShares > 0
      )
    );
  }, [activeShareholders, watchShareClassId]);

  // Get holdings for selected shareholder in selected share class
  // For pledge: show unpledged shares
  // For unpledge: show pledged shares
  const availableHoldings = useMemo(() => {
    if (!watchShareClassId || !holdings.length) return [];

    const classHoldings = holdings.filter(
      (h: ShareHolding) => h.shareClassId === watchShareClassId
    );

    if (pledgeType === "pledge") {
      // Show unpledged shares
      return classHoldings.filter((h: ShareHolding) => !h.isPledged);
    } else {
      // Show pledged shares
      return classHoldings.filter((h: ShareHolding) => h.isPledged);
    }
  }, [holdings, watchShareClassId, pledgeType]);

  // Calculate quantity
  const calculatedQuantity = useMemo(() => {
    if (!watchNumberFrom || !watchNumberTo) return 0;
    return Math.max(0, watchNumberTo - watchNumberFrom + 1);
  }, [watchNumberFrom, watchNumberTo]);

  const handlePledgeTypeChange = (type: PledgeType) => {
    setPledgeType(type);
    setValue("pledgeType", type);
    setValue("numberFrom", undefined as unknown as number);
    setValue("numberTo", undefined as unknown as number);
    if (type === "unpledge") {
      setValue("pledgedTo", "");
    }
  };

  const onSubmit = async (data: PledgeFormData) => {
    try {
      const baseData = {
        shareholderId: data.shareholderId,
        shareClassId: data.shareClassId,
        numberFrom: data.numberFrom,
        numberTo: data.numberTo,
        transactionDate: data.transactionDate,
        notes: data.notes,
      };

      if (data.pledgeType === "pledge") {
        await registerPledge.mutateAsync({
          companyId,
          data: {
            ...baseData,
            pledgedTo: data.pledgedTo || "",
          },
        });
        toast.success(
          `Pantsättning registrerad - ${calculatedQuantity.toLocaleString("sv-SE")} aktier`
        );
      } else {
        await registerUnpledge.mutateAsync({
          companyId,
          data: baseData,
        });
        toast.success(
          `Pantsättning hävd - ${calculatedQuantity.toLocaleString("sv-SE")} aktier`
        );
      }
      handleClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Kunde inte registrera pantsättning";
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    reset();
    setPledgeType("pledge");
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={pledgeType === "pledge" ? "Pantsättning" : "Hävning av pantsättning"}
      size="lg"
    >
      <div className="space-y-6">
        {/* Pledge Type Toggle */}
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => handlePledgeTypeChange("pledge")}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              pledgeType === "pledge"
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-lg ${
                  pledgeType === "pledge"
                    ? "bg-blue-100 text-blue-600"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                <Lock size={20} />
              </div>
              <div>
                <p
                  className={`font-medium ${
                    pledgeType === "pledge" ? "text-blue-900" : "text-gray-900"
                  }`}
                >
                  Pantsättning
                </p>
                <p className="text-xs text-gray-500">Sätt aktier i pant</p>
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => handlePledgeTypeChange("unpledge")}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              pledgeType === "unpledge"
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-lg ${
                  pledgeType === "unpledge"
                    ? "bg-blue-100 text-blue-600"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                <Unlock size={20} />
              </div>
              <div>
                <p
                  className={`font-medium ${
                    pledgeType === "unpledge"
                      ? "text-blue-900"
                      : "text-gray-900"
                  }`}
                >
                  Hävning
                </p>
                <p className="text-xs text-gray-500">Frisläpp pantsatta aktier</p>
              </div>
            </div>
          </button>
        </div>

        {/* Info box */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
            <div>
              <p className="font-medium text-blue-900">
                {pledgeType === "pledge"
                  ? "Vad är pantsättning?"
                  : "Vad är hävning av pantsättning?"}
              </p>
              <p className="text-sm text-blue-700 mt-1">
                {pledgeType === "pledge"
                  ? "När aktier pantsätts används de som säkerhet för ett lån. Aktieägaren behåller äganderätten men panträttens innehavare (panthavaren) har rätt att sälja aktierna om lånet inte betalas."
                  : "När ett lån är återbetalt eller panten inte längre behövs kan pantsättningen hävas och aktierna återgår till att vara opantsatta."}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <input type="hidden" {...register("pledgeType")} />

          {/* Share Class */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Aktieslag *
            </label>
            <select {...register("shareClassId")} className="input">
              <option value="">Välj aktieslag</option>
              {shareClasses.map((sc: ShareClass) => (
                <option key={sc.id} value={sc.id}>
                  {sc.name} ({sc.code}) - {sc.totalShares.toLocaleString("sv-SE")}{" "}
                  aktier
                </option>
              ))}
            </select>
            {errors.shareClassId && (
              <p className="text-sm text-red-600 mt-1">
                {errors.shareClassId.message}
              </p>
            )}
          </div>

          {/* Shareholder */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Aktieägare *
            </label>
            <select {...register("shareholderId")} className="input">
              <option value="">Välj aktieägare</option>
              {shareholdersWithHoldings.map((s: ShareholderWithHoldings) => {
                const holding = s.holdings.find(
                  (h) => h.shareClass.id === watchShareClassId
                );
                return (
                  <option key={s.id} value={s.id}>
                    {s.name}{" "}
                    {holding
                      ? `(${holding.totalShares.toLocaleString("sv-SE")} aktier)`
                      : `(${s.totalShares.toLocaleString("sv-SE")} aktier totalt)`}
                  </option>
                );
              })}
            </select>
            {errors.shareholderId && (
              <p className="text-sm text-red-600 mt-1">
                {errors.shareholderId.message}
              </p>
            )}
          </div>

          {/* Available Holdings Info */}
          {watchShareholderId && watchShareClassId && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={16} />
                <div className="text-sm">
                  <p className="font-medium text-blue-900">
                    {pledgeType === "pledge"
                      ? "Tillgängliga aktier att pantsätta:"
                      : "Pantsatta aktier som kan frisläppas:"}
                  </p>
                  {availableHoldings.length > 0 ? (
                    <ul className="mt-1 space-y-1">
                      {availableHoldings.map((h: ShareHolding, idx: number) => (
                        <li key={idx} className="text-blue-700">
                          Nr {h.numberFrom} - {h.numberTo} (
                          {h.numberTo - h.numberFrom + 1} st)
                          {h.isPledged && h.pledgedTo && (
                            <span className="text-gray-500">
                              {" "}
                              - Panthavare: {h.pledgedTo}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-1 text-blue-700 italic">
                      {pledgeType === "pledge"
                        ? "Inga opantsatta aktier tillgängliga"
                        : "Inga pantsatta aktier att frisläppa"}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Share Number Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Aktienummer från *
              </label>
              <input
                type="number"
                {...register("numberFrom")}
                className="input"
                min={1}
              />
              {errors.numberFrom && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.numberFrom.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Aktienummer till *
              </label>
              <input
                type="number"
                {...register("numberTo")}
                className="input"
                min={1}
              />
              {errors.numberTo && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.numberTo.message}
                </p>
              )}
            </div>
          </div>

          {calculatedQuantity > 0 && (
            <p className="text-sm text-gray-600">
              Antal aktier:{" "}
              <span className="font-medium">
                {calculatedQuantity.toLocaleString("sv-SE")}
              </span>
            </p>
          )}

          {/* Pledged To (only for pledge) */}
          {pledgeType === "pledge" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Panthavare *
              </label>
              <input
                type="text"
                {...register("pledgedTo")}
                className="input"
                placeholder="Namn på panthavare (t.ex. bank eller kreditgivare)"
              />
              <p className="text-xs text-gray-500 mt-1">
                Den person eller organisation som panten ställs till
              </p>
              {errors.pledgedTo && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.pledgedTo.message}
                </p>
              )}
            </div>
          )}

          {/* Transaction Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transaktionsdatum *
            </label>
            <input
              type="date"
              {...register("transactionDate")}
              className="input"
            />
            {errors.transactionDate && (
              <p className="text-sm text-red-600 mt-1">
                {errors.transactionDate.message}
              </p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Anteckningar
            </label>
            <textarea
              {...register("notes")}
              className="input min-h-[80px]"
              placeholder={
                pledgeType === "pledge"
                  ? "Valfria anteckningar om pantsättningen..."
                  : "Valfria anteckningar om hävningen..."
              }
            />
          </div>

          {/* Legal info */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              <strong>Observera:</strong> Pantsättning ska antecknas i aktieboken
              enligt ABL 5 kap. Aktiebrev (om sådana finns) ska överlämnas till
              panthavaren eller denuntieras.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={handleClose}>
              Avbryt
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || calculatedQuantity === 0 || availableHoldings.length === 0}
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Registrerar...
                </>
              ) : pledgeType === "pledge" ? (
                "Registrera pantsättning"
              ) : (
                "Häv pantsättning"
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
